import React, { createContext, useState, useContext, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import authService from '../services/authService';
import { getCustomerData } from '../services/customerService';
import { setupAxiosInterceptors } from '../services/axiosConfig';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [preAuthToken, setPreAuthToken] = useState(null);
    const [center, setCenter] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [features, setFeatures] = useState([]);
    const [role, setRole] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check if token is expired
    const isTokenExpired = (token) => {
        if (!token) return true;
        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            return decoded.exp < currentTime;
        } catch (error) {
            return true;
        }
    };

    // Check if token should be refreshed (less than 125 seconds remaining)
    const shouldRefreshToken = (token) => {
        if (!token) return false;
        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            const timeRemaining = decoded.exp - currentTime;
            // Refresh if less than 125 seconds remaining (before it expires)
            return timeRemaining > 0 && timeRemaining < 125;
        } catch (error) {
            return false;
        }
    };

    // Refresh the access token
    const refreshToken = async () => {
        try {
            const response = await authService.refreshToken(token);
            if (response.data.access_token) {
                const newToken = response.data.access_token;
                setToken(newToken);
                localStorage.setItem('token', newToken);
                return true;
            }
        } catch (error) {
            // If refresh fails, logout the user
            if (error.response?.status === 401) {
                logout(true);
            }
            return false;
        }
    };

    // Define logout function early so it can be used in useEffect hooks
    const logout = React.useCallback((skipApiCall = false) => {
        // Only call API logout if this is user-initiated (not a 401 error response)
        // This prevents infinite loop when token is already expired
        if (!skipApiCall && token) {
            try {
                authService.logout(token);
            } catch (error) {
                // Ignore errors on logout API call - we're logging out anyway
            }
        }

        setUser(null);
        setToken(null);
        setCenter(null);
        setCustomer(null);
        setPermissions([]);
        setFeatures([]);
        setRole(null);
        localStorage.removeItem('token');
        localStorage.removeItem('permissions');
        localStorage.removeItem('features');
        localStorage.removeItem('role');

        // Clear all page-specific cached selections
        // Ultimate Scheduling page
        localStorage.removeItem('ultimateScheduling_selectedTutorIds');
        localStorage.removeItem('ultimateScheduling_selectedDate');
        localStorage.removeItem('ultimateScheduling_currentView');

        // Class Management page
        localStorage.removeItem('classManagement_filters');
        localStorage.removeItem('classManagement_selectedClass');
        localStorage.removeItem('classManagement_sort');
        localStorage.removeItem('classManagement_page');

        // Event List / Scheduling Manager page
        localStorage.removeItem('eventList_selectedTutorId');
        localStorage.removeItem('eventList_selectedCourseId');
        localStorage.removeItem('eventList_startDate');
        localStorage.removeItem('eventList_endDate');
        localStorage.removeItem('eventList_activeShortcut');
        localStorage.removeItem('eventList_sortConfig');

        // Administration page
        localStorage.removeItem('administration_selectedTab');

        // Users Management page
        localStorage.removeItem('usersManagement_filters');
        localStorage.removeItem('usersManagement_currentPage');
        localStorage.removeItem('usersManagement_pageSize');

        // Tutor Profiles page
        localStorage.removeItem('tutorProfiles_filters');
        localStorage.removeItem('tutorProfiles_page');

        // Courses Management page
        localStorage.removeItem('coursesManagement_filters');
        localStorage.removeItem('coursesManagement_page');

        // Family Management page
        localStorage.removeItem('familyManagement_selectedFamily');
        localStorage.removeItem('familyManagement_familySearchTerm');
        localStorage.removeItem('familyManagement_studentSearchTerm');
        localStorage.removeItem('familyManagement_sortConfig');
        localStorage.removeItem('familyManagement_currentPage');
        localStorage.removeItem('familyManagement_itemsPerPage');

        // Add other page caches here as they are implemented
        // Example:
        // localStorage.removeItem('leadPage_selectedStage');
        // localStorage.removeItem('reports_selectedStudent');
        // localStorage.removeItem('studentCheckIn_selectedDate');
    }, [token]);

    const fetchUserContext = async () => {
        try {
            const response = await authService.getUserContext(token);
            const { user: userData, permissions: userPermissions, features: userFeatures, role: userRole } = response.data;


            setUser(userData);
            setPermissions(userPermissions || []);
            setFeatures(userFeatures || []);
            setRole(userRole || null);

            // Store in localStorage for persistence
            localStorage.setItem('permissions', JSON.stringify(userPermissions || []));
            localStorage.setItem('features', JSON.stringify(userFeatures || []));
            localStorage.setItem('role', userRole || '');


            return userData;
        } catch (error) {
            throw error;
        }
    };

    // Set up axios interceptor on mount
    useEffect(() => {
        setupAxiosInterceptors(logout);
    }, [logout]);

    // Check token expiration on mount and periodically
    useEffect(() => {
        if (token && isTokenExpired(token)) {
            logout(true); // Skip API call - token is already expired
            return;
        }

        // Set up interval to check token expiration and refresh if needed
        const interval = setInterval(async () => {
            if (token && isTokenExpired(token)) {
                logout(true); // Skip API call - token is already expired
            } else if (token && shouldRefreshToken(token)) {
                // Proactively refresh token before it expires
                await refreshToken();
            }
        }, 60000); // Check every minute

        return () => clearInterval(interval);
    }, [token, logout]);

    useEffect(() => {
        const fetchUserAndCustomer = async () => {
            // Check if token is expired before fetching
            if (token && isTokenExpired(token)) {
                logout(true); // Skip API call - token is already expired
                setIsLoading(false);
                return;
            }

            if (token && !user) {
                // Only fetch if we have a token but no user data yet
                // This handles page refresh scenarios
                try {
                    const decoded = jwtDecode(token);

                    // Fetch comprehensive user context from /me endpoint
                    const userData = await fetchUserContext();

                    if (decoded.center_id) {
                        setCenter(decoded.center_id);
                    }

                    // Fetch customer data only for roles that need it (SA, CA, CTA)
                    // CTD and below don't need customer branding/settings
                    const userRole = decoded.role || localStorage.getItem('role');
                    if (userRole && ['SA', 'CA', 'CTA'].includes(userRole)) {
                        try {
                            const customerData = await getCustomerData();
                            setCustomer(customerData);
                        } catch (customerError) {
                            setCustomer(null);
                        }
                    }

                } catch (error) {
                    // Only clear auth if it's an auth error (401/403 on /me endpoint)
                    if (error.response?.status === 401 || error.response?.status === 403) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('permissions');
                        localStorage.removeItem('features');
                        setToken(null);
                        setUser(null);
                        setCenter(null);
                        setCustomer(null);
                        setPermissions([]);
                        setFeatures([]);
                    }
                } finally {
                    setIsLoading(false);
                }
            } else {
                // Load from localStorage if available
                const storedPermissions = localStorage.getItem('permissions');
                const storedFeatures = localStorage.getItem('features');
                const storedRole = localStorage.getItem('role');
                if (storedPermissions) setPermissions(JSON.parse(storedPermissions));
                if (storedFeatures) setFeatures(JSON.parse(storedFeatures));
                if (storedRole) setRole(storedRole);
                setIsLoading(false);
            }
        };
        fetchUserAndCustomer();
    }, [token, user, logout]);

    const login = async (email, password) => {
        try {
            const response = await authService.login(email, password);

            if (response.data.access_token) {
                const new_token = response.data.access_token;
                setToken(new_token);
                localStorage.setItem('token', new_token);

                // Fetch user context (user data, permissions, features, role)
                const userContextResponse = await authService.getUserContext(new_token);
                const { user: userData, permissions: userPermissions, features: userFeatures, role: userRole } = userContextResponse.data;

                setUser(userData);
                setPermissions(userPermissions || []);
                setFeatures(userFeatures || []);
                setRole(userRole || null);

                // Store in localStorage for persistence
                localStorage.setItem('permissions', JSON.stringify(userPermissions || []));
                localStorage.setItem('features', JSON.stringify(userFeatures || []));
                localStorage.setItem('role', userRole || '');

                const decoded = jwtDecode(new_token);
                if (decoded.center_id) {
                    setCenter(decoded.center_id);
                }

                // Fetch customer data only for roles that need it (SA, CA, CTA)
                // CTD and below don't need customer branding/settings
                if (userRole && ['SA', 'CA', 'CTA'].includes(userRole)) {
                    try {
                        const customerData = await getCustomerData();
                        setCustomer(customerData);
                    } catch (error) {
                        // Silently fail - customer data is optional
                    }
                }

                return { profiles: null };
            }
            else if (response.data.pre_auth_token) {
                setPreAuthToken(response.data.pre_auth_token);
                return { profiles: response.data.available_profiles };
            }
        } catch (error) {
            throw error;
        }
    };

    const selectProfile = async (center_id, role) => {
        const response = await authService.selectProfile(preAuthToken, center_id, role);
        if (response.data.access_token) {
            const new_token = response.data.access_token;
            setToken(new_token);
            localStorage.setItem('token', new_token);
            setPreAuthToken(null);
            // The useEffect will handle fetching the user
        }
    };

    // Helper functions for permission checking
    const hasPermission = (permission) => {
        return permissions.includes(permission) || permissions.includes('*');
    };

    const hasAnyPermission = (permissionList) => {
        return permissionList.some(p => hasPermission(p));
    };

    const hasFeature = (feature) => {
        return features.includes(feature);
    };

    const value = {
        user,
        token,
        center,
        customer,
        permissions,
        features,
        role,
        isLoading,
        login,
        selectProfile,
        logout,
        hasPermission,
        hasAnyPermission,
        hasFeature,
    };


    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    return useContext(AuthContext);
};
